import React from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ConnectCalendarModalProps {
  isOpen: boolean;
  modalProvider: 'google' | 'outlook';
  manualEmail: string;
  showManualInput: boolean;
  onClose: () => void;
  onOAuthConnect: () => void;
  onManualConnect: (e: React.FormEvent) => void;
  setManualEmail: (email: string) => void;
  setShowManualInput: (show: boolean) => void;
}

export function ConnectCalendarModal({
  isOpen,
  modalProvider,
  manualEmail,
  showManualInput,
  onClose,
  onOAuthConnect,
  onManualConnect,
  setManualEmail,
  setShowManualInput
}: ConnectCalendarModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            style={{
              position: 'relative',
              backgroundColor: 'var(--admin-bg-container, #010204)',
              backgroundImage: 'radial-gradient(circle 250px at top right, rgba(0, 19, 64, 0.2) 0%, transparent 100%)',
              border: '1px solid var(--admin-card-border)',
              borderRadius: '0',
              padding: '28px',
              width: '100%',
              maxWidth: '420px',
              boxShadow: '0 24px 40px rgba(0, 0, 0, 0.35)',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}
          >
            {/* Close Button Top Right */}
            <button
              type="button"
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'none',
                border: 'none',
                color: 'var(--admin-card-subtext)',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent'
              }}
            >
              <X size={18} />
            </button>

            <div>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '20px', fontWeight: 700, color: 'var(--admin-card-text)', margin: 0, paddingRight: '24px' }}>
                Connect {modalProvider === 'google' ? 'Google Calendar' : 'Outlook Calendar'}
              </h3>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: 'var(--admin-card-subtext)', margin: '6px 0 0', lineHeight: 1.4 }}>
                Link your account securely to automatically synchronize upcoming sales meetings and generate AI intelligence briefings.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* Primary Action Button */}
              <button
                type="button"
                onClick={onOAuthConnect}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '0',
                  border: 'none',
                  backgroundColor: '#ffffff',
                  color: '#000000',
                  fontSize: '13px',
                  fontWeight: 700,
                  fontFamily: "'Inter', sans-serif",
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}
              >
                {modalProvider === 'google' ? (
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 23 23" width="18" height="18">
                    <path fill="#f25022" d="M1 1h10v10H1z"/>
                    <path fill="#7fba00" d="M12 1h10v10H12z"/>
                    <path fill="#00a4ef" d="M1 12h10v10H1z"/>
                    <path fill="#ffb900" d="M12 12h10v10H12z"/>
                  </svg>
                )}
                Authorize with {modalProvider === 'google' ? 'Google OAuth' : 'Microsoft Account'}
              </button>

              <div style={{ textAlign: 'center', margin: '4px 0' }}>
                <button
                  type="button"
                  onClick={() => setShowManualInput(!showManualInput)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--admin-card-subtext)',
                    fontSize: '11px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontWeight: 500,
                    fontFamily: "'Inter', sans-serif"
                  }}
                >
                  {showManualInput ? 'Hide simulation option' : 'Or use manual simulation email'}
                </button>
              </div>

              {/* Simulated connection inputs if toggled */}
              {showManualInput && (
                <form onSubmit={onManualConnect} style={{ display: 'flex', flexDirection: 'column', gap: '14px', borderTop: '1px dashed var(--admin-card-border)', paddingTop: '16px' }}>
                  <div>
                    <label style={{ fontSize: '10px', fontWeight: 700, color: 'var(--admin-card-subtext)', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      SIMULATED ACCOUNT EMAIL
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="e.g. demo@company.com"
                      value={manualEmail}
                      onChange={(e) => setManualEmail(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '0',
                        border: '1px solid var(--admin-card-border)',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        color: 'var(--admin-card-text)',
                        fontSize: '13px',
                        fontFamily: "'Inter', sans-serif",
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    style={{
                      width: '100%',
                      padding: '9px',
                      borderRadius: '0',
                      border: 'none',
                      backgroundColor: '#ffffff',
                      color: '#000000',
                      fontSize: '12px',
                      fontWeight: 700,
                      fontFamily: "'Inter', sans-serif",
                      cursor: 'pointer'
                    }}
                  >
                    Create Simulated Link
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
